import React from 'react';
import { useParams } from 'react-router-dom';
import {
  useResponsive, useToast, useOnlineStatus, EmptyState, SkeletonCard, ConfirmDialog, Alert,
} from '@parsina/framework';
import { Stethoscope } from 'lucide-react';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useEncounter, SaveState } from '../../hooks/useEncounter.js';
import { useCopilot } from '../../hooks/useCopilot.js';
import { useMe } from '../../hooks/useMe.js';
import {
  errorCode, errorMessageKey, isUnavailable, ERROR_CODES,
} from '../../lib/bffClient.js';
import { readJson, writeJson, VISIT_RAIL_KEY } from '../../lib/drafts.js';
import { useShortcut, useEscapeCloser } from '../../lib/shortcuts.js';
import ErrorState from '../../components/common/ErrorState.jsx';
import { SoapReviewSheet, OrderSuggestionSheet } from '../../components/clinical/SuggestionReviewSheet.jsx';
import { DifferentialSheet, diagnosisFromDifferential } from '../../components/clinical/DiagnosisPanel.jsx';
import InteractionSheet from '../../components/clinical/InteractionSheet.jsx';
import PrintPrescription from '../../components/clinical/PrintPrescription.jsx';
import CloseVisitDialog from './CloseVisitDialog.jsx';
import VisitWeb from './Visit.web.jsx';
import VisitMobile from './Visit.mobile.jsx';
import {
  WORK_TABS, DEFAULT_WORK_TAB, DEFAULT_COPILOT_TAB,
  closureWarnings, cancelCounts, summarySeed,
} from './Visit.shared.js';

// The visit workspace — `/patients/:caseId/visit/:encounterId`, core.md §7.5.
//
// This file is the WHOLE of the screen's behaviour and none of its layout. The
// two surfaces below it (`Visit.web.jsx`, `Visit.mobile.jsx`) receive one `vm`
// object and render it; every piece of state, every mutation, every sheet and
// every keyboard binding lives here.
//
// That split is not tidiness. Three of this screen's rules are cross-cutting and
// cannot live inside a tab:
//
//   • The closure dialog warns about «pending AI review sheets open», so the
//     sheets' open state has to be visible from the header.
//   • `Ctrl+1..4` switches work tabs and `Ctrl+S` force-saves; both must work
//     from anywhere on the screen, including from inside the copilot composer.
//   • The autosave has to be FLUSHED before `endEncounter`, which is a header
//     action operating on a hook the note tab renders.
//
// ── The shortcuts go through the registry, never through `window` ────────
//
// `src/lib/shortcuts.js` is the one keydown listener in the app, and it is what
// the `?` overlay enumerates. A per-screen `window.addEventListener('keydown')`
// would be invisible to that overlay and would survive one render too many on
// every route change — which is how two screens end up fighting over `Esc`.

const RAIL_COLLAPSED_KEY = 'collapsed';
const RAIL_WIDTH = 300;
const COPILOT_WIDTH = 360;

// The keys `useEncounter` toasts by name. Everything it says is a visit toast
// except the closed-visit refusal, which is the app-wide error copy (§14).
const HOOK_TOAST_KEYS = { encounterClosed: 'errors.encounterClosed' };
const hookToastKey = (key) => HOOK_TOAST_KEYS[key] || `visit.toast.${key}`;

/** The loading state: the three regions as skeletons on a desktop, stacked on a phone (§7.5). */
function VisitSkeleton({ mobile, columns }) {
  const column = (n) => Array.from({ length: n }, (_, i) => <SkeletonCard key={i} />);
  if (mobile) {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12 }}
        data-testid="visit-loading"
        data-layout="mobile"
      >
        {column(3)}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 12, padding: 12 }} data-testid="visit-loading" data-layout="web">
      <div style={{ width: RAIL_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="visit-skeleton-rail">
        {column(3)}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="visit-skeleton-work">
        {column(2)}
      </div>
      {columns === 3 && (
        <div style={{ width: COPILOT_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="visit-skeleton-copilot">
          {column(2)}
        </div>
      )}
    </div>
  );
}

export default function VisitScreen() {
  const { caseId, encounterId } = useParams();
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  // 769–1279px keeps the two working columns and moves the copilot into a
  // right-edge drawer: three columns in 1000px leaves the note 340px wide,
  // which is not a column anybody writes a paragraph in.
  const { isMobile: isNarrowDesktop } = useResponsive(1279);
  const navigate = useAppNavigate();
  const online = useOnlineStatus();
  const { me, balance } = useMe();

  // The kit's toast throws outside its provider. A workspace that refuses to
  // mount because nothing was there to say «ذخیره نشد» would be a worse failure
  // than a silent toast, so it is optional here and everywhere below.
  let toast = null;
  try { toast = useToast(); } catch { toast = null; }

  const toastAdapter = React.useMemo(() => ({
    info: (key) => toast?.info?.(t(hookToastKey(key))),
    error: (key) => toast?.error?.(t(hookToastKey(key))),
  }), [toast, t]);

  const enc = useEncounter(encounterId, caseId, { onToast: toastAdapter });

  // ── A refused AI call is said WHERE THE BUTTON WAS, not only in the panel ──
  //
  // The copilot column renders «شارژ پارسه» and the error card, but the buttons
  // that spend live in the four work tabs, and the column is collapsed, a
  // closed drawer, or another segment on a phone exactly when the student is
  // looking at one of them. core.md §7.5: the AI buttons «route to شارژ پارسه
  // on click» — so the toast carries that route, and an INVALID_INPUT (no
  // formulary drug to check) carries the BFF's own sentence.
  const onCopilotRefusal = React.useCallback(({ reason, error }) => {
    if (!toast) return;
    if (reason === 'credits') {
      toast.error?.((
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
          data-testid="copilot-topup-toast"
        >
          {t('assistant.noCredits.title')}
          <button
            type="button"
            onClick={() => navigate('credits')}
            style={{
              border: '1px solid currentColor', background: 'none', color: 'inherit', font: 'inherit',
              borderRadius: 999, padding: '2px 10px', cursor: 'pointer', fontWeight: 700,
            }}
            data-testid="copilot-topup-toast-action"
          >
            {t('assistant.noCredits.action')}
          </button>
        </span>
      ), { duration: 8000 });
      return;
    }
    const invalid = errorCode(error) === ERROR_CODES.INVALID_INPUT;
    toast.error?.(invalid && error?.message ? error.message : t(errorMessageKey(error)));
  }, [toast, t, navigate]);

  const copilot = useCopilot(encounterId, { balance, caseId, onRefusal: onCopilotRefusal });

  const [workTab, setWorkTab] = React.useState(DEFAULT_WORK_TAB);
  const [copilotTab, setCopilotTab] = React.useState(DEFAULT_COPILOT_TAB);
  const [mobileSegment, setMobileSegment] = React.useState('note');
  const [railCollapsed, setRailCollapsed] = React.useState(
    () => !!(readJson(VISIT_RAIL_KEY, { kind: 'local', fallback: {} }) || {})[RAIL_COLLAPSED_KEY],
  );
  const [copilotOpen, setCopilotOpen] = React.useState(true);
  const [writeError, setWriteError] = React.useState(null);
  // A clinical write that failed because the store could not be reached. The
  // banner below reads it beside the browser's own offline flag, and it is
  // cleared by the next write that goes through — or the next note save that does.
  const [storeDown, setStoreDown] = React.useState(false);
  React.useEffect(() => {
    if (enc.saveState === SaveState.SAVED) setStoreDown(false);
  }, [enc.saveState]);

  const [sheets, setSheets] = React.useState({
    differential: false,
    soapReview: false,
    orderSuggest: false,
    interactions: false,
    print: false,
  });
  const [closeOpen, setCloseOpen] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);

  const openSheet = React.useCallback((id) => setSheets((s) => ({ ...s, [id]: true })), []);
  const closeSheet = React.useCallback((id) => setSheets((s) => ({ ...s, [id]: false })), []);
  const anySheetOpen = Object.values(sheets).some(Boolean);

  // The one global `Esc` closes whatever is on top; these register the closers.
  useEscapeCloser(sheets.soapReview, () => closeSheet('soapReview'));
  useEscapeCloser(sheets.differential, () => closeSheet('differential'));
  useEscapeCloser(sheets.orderSuggest, () => closeSheet('orderSuggest'));
  useEscapeCloser(sheets.interactions, () => closeSheet('interactions'));
  useEscapeCloser(sheets.print, () => closeSheet('print'));

  const toggleRail = React.useCallback(() => {
    setRailCollapsed((prev) => {
      const next = !prev;
      writeJson(VISIT_RAIL_KEY, { [RAIL_COLLAPSED_KEY]: next }, { kind: 'local' });
      return next;
    });
  }, []);

  // On a 769–1279px viewport the copilot is a DRAWER, and a drawer that is open
  // on arrival covers the note the student came here to write. It opens on
  // request there and stays a column everywhere wider.
  React.useEffect(() => {
    if (isNarrowDesktop && !isMobile) setCopilotOpen(false);
  }, [isNarrowDesktop, isMobile]);

  const readOnly = enc.isClosed;

  // ── Writes, and the one error that changes the screen ───────────────────
  //
  // `ENCOUNTER_CLOSED` means the visit was closed elsewhere (another tab) while
  // this one was still editing. It is not a validation failure and not a retry:
  // the screen re-reads the encounter, which flips it to read-only with
  // «بازگشایی», and says so.
  //
  // `rethrow` is for a caller that has its own place to say it — the result
  // sheet keeps itself open with the message instead of closing over a banner
  // it has just covered.
  const guard = React.useCallback((fn, { rethrow = false } = {}) => async (...args) => {
    try {
      setWriteError(null);
      const out = await fn(...args);
      setStoreDown(false);
      return out;
    } catch (err) {
      setWriteError(err);
      if (errorCode(err) === ERROR_CODES.ENCOUNTER_CLOSED) enc.reload();
      if (isUnavailable(err)) setStoreDown(true);
      if (rethrow) throw err;
      return null;
    }
  }, [enc]);

  const askCopilot = React.useCallback(async (question) => {
    await copilot.ask(question);
  }, [copilot]);

  const requestSoapSuggestion = React.useCallback(async () => {
    const payload = await copilot.suggestSoap();
    if (payload) openSheet('soapReview');
  }, [copilot, openSheet]);

  const requestDifferential = React.useCallback(async () => {
    const payload = await copilot.runDifferential();
    if (payload) openSheet('differential');
  }, [copilot, openSheet]);

  const requestOrderSuggestions = React.useCallback(async () => {
    const payload = await copilot.suggestOrders();
    if (payload) openSheet('orderSuggest');
  }, [copilot, openSheet]);

  const requestInteractions = React.useCallback(async () => {
    const payload = await copilot.checkInteractions();
    if (payload) openSheet('interactions');
  }, [copilot, openSheet]);

  // ── Closure ─────────────────────────────────────────────────────────────

  const warnings = React.useMemo(() => closureWarnings({
    soap: enc.soap,
    diagnoses: enc.encounter?.diagnoses || [],
    openSheets: anySheetOpen ? 1 : 0,
  }), [enc.soap, enc.encounter, anySheetOpen]);

  const endVisit = React.useCallback(async (summary) => {
    // FLUSH FIRST. See CloseVisitDialog's header for why the order is fixed.
    const flushed = await enc.flush();
    if (flushed && flushed.ok === false) return { ok: false, error: flushed.error };
    try {
      await enc.endEncounter(summary);
    } catch (err) {
      return { ok: false, error: err };
    }
    setCloseOpen(false);
    toast?.success?.(t('visit.toast.ended'));
    navigate('patient', { caseId, tab: 'visits' });
    return { ok: true };
  }, [enc, navigate, caseId, toast, t]);

  const cancelVisit = React.useCallback(async () => {
    try {
      await enc.cancelEncounter();
      setCancelOpen(false);
      toast?.info?.(t('visit.toast.cancelled'));
      navigate('patient', { caseId, tab: 'visits' });
    } catch (err) {
      setWriteError(err);
      setCancelOpen(false);
    }
  }, [enc, navigate, caseId, toast, t]);

  const reopen = React.useCallback(async () => {
    try { await enc.reopenEncounter(); } catch (err) { setWriteError(err); }
  }, [enc]);

  // ── Shortcuts (core.md §7.5; listed in the `?` overlay) ─────────────────

  const group = t('visit.shortcuts.group');
  useShortcut('ctrl+s', () => { enc.flush(); }, { label: t('visit.shortcuts.save'), group, firesInInput: true });
  useShortcut('ctrl+1', () => setWorkTab(WORK_TABS[0]), { label: t('visit.shortcuts.tab1'), group });
  useShortcut('ctrl+2', () => setWorkTab(WORK_TABS[1]), { label: t('visit.shortcuts.tab2'), group });
  useShortcut('ctrl+3', () => setWorkTab(WORK_TABS[2]), { label: t('visit.shortcuts.tab3'), group });
  useShortcut('ctrl+4', () => setWorkTab(WORK_TABS[3]), { label: t('visit.shortcuts.tab4'), group });
  useShortcut('ctrl+shift+e', () => { if (!readOnly) setCloseOpen(true); }, {
    label: t('visit.shortcuts.end'), group, firesInInput: true,
  });

  // ── The states that are not the workspace ───────────────────────────────

  if (enc.loading) {
    return <VisitSkeleton mobile={isMobile} columns={isNarrowDesktop ? 2 : 3} />;
  }

  // Not found, not mine, and «the URL's patient is not this visit's patient» are
  // ONE screen with ONE sentence — anything else is an oracle (core.md §3.2).
  if (enc.notFound) {
    return (
      <EmptyState
        icon={<Stethoscope size={44} strokeWidth={1.5} aria-hidden />}
        title={t('visit.notFound')}
        action={(
          <button
            type="button"
            onClick={() => navigate('patient', { caseId })}
            style={{ border: 0, background: 'none', font: 'inherit', cursor: 'pointer', color: 'inherit' }}
            data-testid="visit-notfound-back"
          >
            {t('visit.backToRecord')}
          </button>
        )}
        data-testid="visit-not-found"
      />
    );
  }

  if (enc.error) {
    return <ErrorState error={enc.error} onRetry={enc.reload} fill data-testid="visit-error" />;
  }

  const vm = {
    caseId,
    encounterId,
    encounter: enc.encounter,
    patient: enc.record?.case || null,
    record: enc.record,
    readOnly,
    offline: online.isOffline,
    balance,

    soap: enc.soap,
    setSoapField: enc.setSoapField,
    saveState: enc.saveState,
    retrySave: enc.retrySave,

    addDiagnosis: guard(enc.addDiagnosis),
    updateDiagnosis: guard(enc.updateDiagnosis),
    deleteDiagnosis: guard(enc.deleteDiagnosis),
    reorderDiagnoses: guard(enc.reorderDiagnoses),
    addRxItem: guard(enc.addRxItem),
    deleteRxItem: guard(enc.deleteRxItem),
    addOrder: guard(enc.addOrder),
    cancelOrder: guard(enc.cancelOrder),
    recordOrderResult: guard(enc.recordOrderResult, { rethrow: true }),
    addVitals: guard(enc.addVitals),

    copilot,
    askCopilot,
    requestSoapSuggestion,
    requestDifferential,
    requestOrderSuggestions,
    requestInteractions,
    goToCredits: () => navigate('credits'),

    sheets,
    openSheet,
    closeSheet,

    workTab,
    setWorkTab,
    copilotTab,
    setCopilotTab,
    mobileSegment,
    setMobileSegment,
    railCollapsed,
    toggleRail,
    copilotOpen,
    setCopilotOpen,
    isNarrowDesktop: isNarrowDesktop && !isMobile,

    onBack: () => navigate('patient', { caseId, tab: 'visits' }),
    onEnd: () => setCloseOpen(true),
    onReopen: reopen,
    onCancel: () => setCancelOpen(true),
  };

  const Surface = isMobile ? VisitMobile : VisitWeb;
  const counts = cancelCounts(enc.encounter);

  return (
    <>
      {/* The store is unreachable — the browser says so, or a save just found
          out: the note keeps being typed and keeps being mirrored into
          localStorage, which is exactly what §7.5 asks for. */}
      {(online.isOffline || storeDown || isUnavailable(enc.saveError)) && (
        <Alert variant="warning" title={t('visit.offline.title')} data-testid="visit-offline">
          {t('visit.offline.body')}
        </Alert>
      )}
      {writeError && (
        <Alert
          variant="error"
          title={t(errorMessageKey(writeError))}
          data-testid="visit-write-error"
        >
          {writeError.message}
        </Alert>
      )}

      <Surface vm={vm} />

      {/* Gated on read-only as well as on its own flag: a suggestion cannot be
          applied to a closed visit, so the sheet that applies it is not shown
          over one — including a sheet that was open when the visit closed. */}
      <SoapReviewSheet
        open={sheets.soapReview && !readOnly}
        onClose={() => closeSheet('soapReview')}
        suggestion={copilot.soapSuggestion}
        current={enc.soap}
        onApply={(field, mode) => {
          // The click — not the answer arriving — is what writes (core.md §3.4).
          enc.applySoap({ [field]: copilot.soapSuggestion?.[field] }, mode);
        }}
      />

      <OrderSuggestionSheet
        open={sheets.orderSuggest}
        onClose={() => closeSheet('orderSuggest')}
        suggestion={copilot.orderSuggestion}
        onAdd={async (items) => {
          for (const item of items) {
            // Sequential: they land in one list on one encounter, and the order
            // the student ticked them in is the order they should appear in.
            // eslint-disable-next-line no-await-in-loop
            await guard(enc.addOrder)({
              orderType: item.orderType || 'lab',
              title: item.title,
              code: item.code || null,
              category: item.category || null,
              urgency: item.urgency || 'routine',
              notes: item.reason || null,
              origin: 'ai',
            });
          }
          closeSheet('orderSuggest');
        }}
      />

      <DifferentialSheet
        open={sheets.differential}
        onClose={() => closeSheet('differential')}
        result={copilot.differential}
        readOnly={readOnly}
        onAdd={(item) => guard(enc.addDiagnosis)(diagnosisFromDifferential(item))}
      />

      <InteractionSheet
        open={sheets.interactions}
        onClose={() => closeSheet('interactions')}
        result={copilot.interactions}
      />

      <PrintPrescription
        open={sheets.print}
        onClose={() => closeSheet('print')}
        items={enc.encounter?.rxItems || []}
        patient={enc.record?.case || null}
        me={me}
        encounter={enc.encounter}
      />

      <CloseVisitDialog
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        warnings={warnings}
        defaultSummary={summarySeed(enc.soap)}
        onConfirm={endVisit}
      />

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={(v) => { if (!v) setCancelOpen(false); }}
        title={t('visit.cancelConfirm.title')}
        description={t('visit.cancelConfirm.body', {
          dx: counts.diagnoses,
          rx: counts.rx,
          orders: counts.orders,
        })}
        destructive
        confirmLabel={t('visit.cancelConfirm.confirm')}
        cancelLabel={t('common.actions.cancel')}
        onConfirm={cancelVisit}
        confirmTestId="visit-cancel-confirm"
        data-testid="visit-cancel-dialog"
      />
    </>
  );
}
